// useCommentOutbox — an optimistic, fault-tolerant "outbox" for posting comments (roadmap #6). Shared by
// the guest book (events-web) and the host/crew comments drawer (apps/app). Posting is instant: the draft
// appears in the thread as "sending" while the request runs; on success it's removed (the real server
// comment takes its place via onSent); on failure it flips to "failed" with a Resend/Discard affordance.
// The queue is persisted to localStorage, so a page reload restores unsent drafts (as "failed", since any
// in-flight request was lost) and the user can resend — nothing typed is ever silently dropped.
//
// Lives in @em/blocks only because it's the shared React package both apps depend on; it renders nothing.
// `send` returns any OpResult-shaped result ({ code, data }) so the hook needs no @sdflc dependency.
import { useCallback, useEffect, useRef, useState } from 'react';

export type OutboxStatus = 'sending' | 'failed';

export interface OutboxComment {
  /** Client-side id (not a server id) while the draft is unsent. */
  tempId: string;
  commentText: string;
  /** ISO timestamp the draft was created (used for ordering + display). */
  createdAt: string;
  status: OutboxStatus;
  /** Context needed to (re)send + to scope. */
  entityKind: string;
  entityId?: string | null;
  channelCommentId?: string | null;
  visibility?: 'guest' | 'crew' | null;
  commenterName?: string | null;
}

export type OutboxDraft = Omit<OutboxComment, 'tempId' | 'status' | 'createdAt'> & { createdAt?: string };

/** The minimal OpResult shape the outbox needs — any @sdflc OpResult satisfies it. */
export interface OutboxSendResult {
  code: number;
  data?: unknown;
}

let seq = 0;
// Called only from client event handlers (submit/resend), never during render — Date.now is safe here.
const nextTempId = (): string => `tmp_${Date.now().toString(36)}_${(seq++).toString(36)}`;

export interface UseCommentOutboxOptions {
  /** localStorage key — scope it to the thread (event + entityKind + entityId) so each thread is separate. */
  storageKey: string;
  /** Send one draft; return its OpResult-shaped result (code 0 = accepted; data = the created comment). */
  send: (draft: OutboxComment) => Promise<OutboxSendResult>;
  /** Called after the server accepts a draft — merge result.data (the real comment) into your list. */
  onSent?: (result: OutboxSendResult, draft: OutboxComment) => void;
}

export interface UseCommentOutboxReturn {
  /** Unsent drafts, newest first — render these at the top of the thread. */
  pending: OutboxComment[];
  /** Enqueue + send a new draft (returns immediately). */
  submit: (draft: OutboxDraft) => void;
  /** Retry a failed draft. */
  resend: (tempId: string) => void;
  /** Drop a draft without sending. */
  discard: (tempId: string) => void;
}

export function useCommentOutbox({ storageKey, send, onSent }: UseCommentOutboxOptions): UseCommentOutboxReturn {
  const [pending, setPending] = useState<OutboxComment[]>([]);
  const sendRef = useRef(send);
  const onSentRef = useRef(onSent);
  const pendingRef = useRef<OutboxComment[]>([]);
  // ⚠ The drafts whose request is out right now — a second send of the same draft is refused. A comment was saved TWICE
  //   in the same second (owner, 2026-10-23): `resend` sent from inside a state updater, which React runs twice in
  //   development, and nothing stopped a draft already on its way from going again.
  const inFlightRef = useRef<Set<string>>(new Set());
  sendRef.current = send;
  onSentRef.current = onSent;
  pendingRef.current = pending;

  // Restore drafts left from a previous session. Anything unsent is marked "failed" (its request, if any,
  // was lost on reload) so the user can resend rather than lose what they typed.
  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
      const saved: OutboxComment[] = raw ? JSON.parse(raw) : [];
      setPending(Array.isArray(saved) ? saved.map((c) => ({ ...c, status: 'failed' as OutboxStatus })) : []);
    } catch {
      setPending([]);
    }
  }, [storageKey]);

  const setAndPersist = useCallback(
    (updater: (prev: OutboxComment[]) => OutboxComment[]) => {
      setPending((prev) => {
        const next = updater(prev);
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // ignore storage failures (private mode / quota)
        }
        return next;
      });
    },
    [storageKey],
  );

  const runSend = useCallback(
    async (draft: OutboxComment) => {
      if (inFlightRef.current.has(draft.tempId)) {
        return;
      }

      inFlightRef.current.add(draft.tempId);
      setAndPersist((prev) => prev.map((c) => (c.tempId === draft.tempId ? { ...c, status: 'sending' } : c)));
      let result: OutboxSendResult;
      try {
        result = await sendRef.current(draft);
      } catch {
        inFlightRef.current.delete(draft.tempId);
        setAndPersist((prev) => prev.map((c) => (c.tempId === draft.tempId ? { ...c, status: 'failed' } : c)));
        return;
      }
      inFlightRef.current.delete(draft.tempId);
      if (result.code === 0) {
        setAndPersist((prev) => prev.filter((c) => c.tempId !== draft.tempId));
        onSentRef.current?.(result, draft);
      } else {
        setAndPersist((prev) => prev.map((c) => (c.tempId === draft.tempId ? { ...c, status: 'failed' } : c)));
      }
    },
    [setAndPersist],
  );

  const submit = useCallback(
    (draft: OutboxDraft) => {
      const item: OutboxComment = {
        ...draft,
        tempId: nextTempId(),
        createdAt: draft.createdAt ?? new Date().toISOString(),
        status: 'sending',
      };
      setAndPersist((prev) => [item, ...prev]);
      void runSend(item);
    },
    [runSend, setAndPersist],
  );

  // ⚠ Read from the ref, never send from inside a state updater: React may run an updater twice.
  const resend = useCallback(
    (tempId: string) => {
      const item = pendingRef.current.find((c) => c.tempId === tempId);

      if (item) {
        void runSend(item);
      }
    },
    [runSend],
  );

  const discard = useCallback(
    (tempId: string) => {
      setAndPersist((prev) => prev.filter((c) => c.tempId !== tempId));
    },
    [setAndPersist],
  );

  return { pending, submit, resend, discard };
}
